import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Compass, ArrowUpRight, Sparkles, CircleDashed, CheckCircle2, FileDown, Camera, TrendingUp, TrendingDown, Minus, ShieldCheck, ShieldAlert } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

const Sparkline = ({ values }) => {
  if (!values || values.length < 2) return null;
  const w = 96, h = 28, min = Math.min(...values), max = Math.max(...values), span = max - min || 1;
  const pts = values.map((v, i) => `${(i / (values.length - 1)) * w},${h - ((v - min) / span) * (h - 4) - 2}`).join(" ");
  const last = pts.split(" ").slice(-1)[0].split(",");
  return (
    <svg width={w} height={h} className="overflow-visible" data-testid="e360-estate-sparkline">
      <polyline points={pts} fill="none" stroke="#b89bff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last[0]} cy={last[1]} r="2.5" fill="#fff" />
    </svg>
  );
};

const bandColor = (band) => ({
  Emerging: "bg-rose-100 text-rose-700", Developing: "bg-amber-100 text-amber-700",
  Established: "bg-emerald-100 text-emerald-700", Advanced: "bg-emerald-200 text-emerald-800",
}[band] || "bg-zinc-100 text-zinc-600");

const barColor = (band) => ({
  Emerging: "bg-rose-400", Developing: "bg-amber-400",
  Established: "bg-emerald-400", Advanced: "bg-emerald-600",
}[band] || "bg-zinc-300");

const Stat = ({ label, value, accent }) => (
  <div>
    <div className="text-[10px] tracking-[0.16em] uppercase text-zinc-400">{label}</div>
    <div className={`text-2xl font-semibold ${accent ? "text-[var(--e360-primary)]" : "text-black"}`}>{value}</div>
  </div>
);

export default function ClientSpine({ clientId }) {
  const [spine, setSpine] = useState(null);
  const [err, setErr] = useState(false);
  const [snapping, setSnapping] = useState(false);

  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }); } catch { return ""; } };

  const load = () => {
    setSpine(null); setErr(false);
    api.get(`/admin/enterprise360/clients/${clientId}/spine`)
      .then(({ data }) => setSpine(data))
      .catch(() => setErr(true));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const captureSnapshot = async () => {
    setSnapping(true);
    try {
      const { data } = await api.post(`/admin/enterprise360/clients/${clientId}/snapshot`);
      setSpine((s) => (s ? { ...s, trend: data.trend } : s));
      toast.success("Maturity snapshot captured");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not capture snapshot"); }
    finally { setSnapping(false); }
  };

  if (err) return null;
  if (!spine) return <div className="text-zinc-400 text-sm mb-5" data-testid="e360-spine-loading">Loading maturity spine…</div>;

  const { summary, lenses, next_best: recs, value_streams: streams, trend } = spine;
  const up = spine.upgrade_path;
  const eg = spine.evidence_gate;
  const idx = summary.avg_normalised;
  const estateBand = idx == null ? null : idx < 25 ? "Emerging" : idx < 50 ? "Developing" : idx < 75 ? "Established" : "Advanced";
  const delta = trend?.delta;

  return (
    <div data-testid="e360-maturity-spine" className="border border-zinc-200 rounded-xl p-5 mb-6 bg-gradient-to-br from-white to-zinc-50">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Compass size={15} className="text-[var(--e360-primary)]" />
            <span className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[var(--e360-primary)]">EKSELENS360 Maturity Spine</span>
          </div>
          <p className="text-sm text-zinc-500 mb-5">One joined-up read of this client across all 12 functional lenses and the value-stream engine — with the next-best assessment to run.</p>
        </div>
        <a data-testid="e360-cross-sell-brief" href={`${BASE}/api/admin/enterprise360/clients/${clientId}/cross-sell-brief.pdf`}
          target="_blank" rel="noreferrer"
          className="shrink-0 inline-flex items-center gap-2 border border-[var(--e360-primary)] text-[var(--e360-primary)] px-3.5 py-2 text-[11px] tracking-[0.14em] uppercase rounded hover:bg-[var(--e360-primary)]/5 transition-colors">
          <FileDown size={14} /> Download brief (PDF)
        </a>
      </div>

      {/* Estate maturity hero — one number a board instantly grasps */}
      <div className="flex items-center gap-5 mb-6 p-4 rounded-lg bg-[#0B0B0C] text-white" data-testid="e360-estate-maturity">
        <div className="flex flex-col items-center justify-center w-24 shrink-0">
          <span className="text-4xl font-semibold leading-none" data-testid="e360-estate-index">{idx == null ? "—" : idx}</span>
          <span className="text-[10px] tracking-[0.16em] uppercase text-zinc-400 mt-1">/ 100</span>
        </div>
        <div className="flex-1">
          <div className="text-[10px] tracking-[0.18em] uppercase text-[#b89bff] mb-1">Estate maturity</div>
          {estateBand
            ? <div className="text-lg font-medium">{estateBand} <span className="text-zinc-400 text-sm font-normal">· averaged across {summary.assessed_count} assessed lens{summary.assessed_count === 1 ? "" : "es"}</span></div>
            : <div className="text-sm text-zinc-400">No lenses assessed yet — run a first diagnostic to set the baseline.</div>}
          <div className="mt-2 h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div className="h-1.5 rounded-full bg-[#b89bff]" style={{ width: `${idx || 0}%` }} />
          </div>
        </div>
        {/* Trend sparkline + delta + capture */}
        <div className="flex flex-col items-end gap-1.5 shrink-0" data-testid="e360-estate-trend">
          {trend?.series?.length > 1 && <Sparkline values={trend.series} />}
          {delta != null && (
            <span className={`text-[12px] inline-flex items-center gap-1 ${delta.change > 0 ? "text-emerald-400" : delta.change < 0 ? "text-rose-400" : "text-zinc-400"}`} data-testid="e360-estate-delta">
              {delta.change > 0 ? <TrendingUp size={13} /> : delta.change < 0 ? <TrendingDown size={13} /> : <Minus size={13} />}
              {delta.change > 0 ? "+" : ""}{delta.change} since {fmtDate(delta.since)}
            </span>
          )}
          <button onClick={captureSnapshot} disabled={snapping} data-testid="e360-capture-snapshot"
            className="text-[10px] tracking-[0.14em] uppercase text-zinc-300 hover:text-white inline-flex items-center gap-1 disabled:opacity-50">
            <Camera size={12} /> {snapping ? "Capturing…" : "Capture snapshot"}
          </button>
        </div>
      </div>

      {/* Evidence Gate posture — Sprint 0 portfolio signal */}
      {eg && eg.status !== "none" && (
        <div data-testid="e360-evidence-gate"
          className={`flex items-center justify-between gap-3 mb-6 px-4 py-2.5 rounded-lg border text-[12px] ${eg.status === "released" ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
          <div className="flex items-center gap-2">
            {eg.status === "released" ? <ShieldCheck size={15} /> : <ShieldAlert size={15} />}
            <span>
              <strong>Evidence Gate</strong> · lowest confidence {eg.min_pct}% → ≥{eg.target_pct}% target ·{" "}
              {eg.status === "released"
                ? `${eg.released_count}/${eg.total} released for mobilise`
                : `${eg.at_risk_count} of ${eg.total} assessment${eg.total === 1 ? "" : "s"} below target — Sprint 0 recommended`}
            </span>
          </div>
          {eg.status !== "released" && (
            <span className="text-[11px] font-medium uppercase tracking-wider shrink-0">Sell Sprint 0</span>
          )}
        </div>
      )}

      {/* Summary stats */}
      <div className="flex flex-wrap gap-8 mb-6" data-testid="e360-spine-summary">
        <Stat label="Lenses assessed" value={`${summary.assessed_count}/${summary.total_lenses}`} />
        <Stat label="Coverage" value={`${summary.coverage_pct}%`} accent />
        <Stat label="Avg maturity" value={summary.avg_normalised != null ? `${summary.avg_normalised}/100` : "—"} />
        <Stat label="Weakest" value={summary.weakest || "—"} />
        <Stat label="Strongest" value={summary.strongest || "—"} />
      </div>

      {/* Engagement brief carried forward from the won proposal (no re-keying) */}
      {spine.engagement_brief && (
        <div data-testid="e360-engagement-brief" className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
          <div className="flex items-center gap-1.5 text-[11px] tracking-[0.16em] uppercase text-emerald-700 font-semibold mb-1.5">
            <CheckCircle2 size={13} /> Engagement brief · carried from proposal
          </div>
          <div className="text-[12px] text-zinc-700">
            <b>{spine.engagement_brief.proposal_title || "Proposal"}</b>
            {spine.engagement_brief.proposal_version ? ` · v${spine.engagement_brief.proposal_version}` : ""}
            {spine.engagement_brief.proposal_status ? ` · ${spine.engagement_brief.proposal_status}` : ""}
          </div>
          {spine.engagement_brief.scope && <p className="text-[12px] text-zinc-600 mt-1.5"><span className="uppercase tracking-wider text-[10px] text-zinc-400">Scope</span><br />{spine.engagement_brief.scope}</p>}
          {(spine.engagement_brief.deliverables || []).length > 0 && (
            <ul className="mt-1.5 space-y-0.5">
              {spine.engagement_brief.deliverables.slice(0, 6).map((x, i) => (
                <li key={i} className="text-[12px] text-zinc-600 flex items-start gap-1.5"><ArrowUpRight size={12} className="text-emerald-600 mt-0.5 shrink-0" />{x}</li>
              ))}
            </ul>
          )}
          {(spine.engagement_brief.pricing || spine.engagement_brief.timeline) && (
            <div className="flex flex-wrap gap-5 mt-2.5 text-[12px] text-zinc-700">
              {spine.engagement_brief.pricing && <span><span className="text-[10px] uppercase tracking-wider text-zinc-400">Price</span><br />{spine.engagement_brief.pricing}</span>}
              {spine.engagement_brief.timeline && <span><span className="text-[10px] uppercase tracking-wider text-zinc-400">Timeline</span><br />{spine.engagement_brief.timeline}</span>}
            </div>
          )}
        </div>
      )}

      {/* LITE → Full upgrade path (proof-led cross-sell) */}
      {up?.recommend && (
        <div data-testid="e360-upgrade-path" className="mb-6 rounded-xl border border-[var(--e360-primary)]/40 bg-gradient-to-br from-[var(--e360-primary)]/[0.06] to-transparent p-4">
          <div className="flex items-center gap-1.5 text-[11px] tracking-[0.16em] uppercase text-[var(--e360-primary)] font-semibold mb-1.5">
            <ShieldAlert size={13} /> Upgrade path · {up.from} → {up.to}
          </div>
          <p data-testid="e360-upgrade-headline" className="text-[13px] text-zinc-800 leading-snug">{up.headline}</p>
          <div className="flex flex-wrap gap-4 mt-3">
            <Stat label="Self-declared" value={`${up.self_declared_count}/${up.streams_total}`} />
            <Stat label="Evidenced" value={`${up.evidenced_streams}/${up.streams_total}`} accent />
            {up.value_at_stake && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.12em] text-zinc-400">£-at-stake <span className="normal-case tracking-normal text-amber-600">· internal</span></div>
                <div data-testid="e360-upgrade-value" className="text-lg font-bold text-[var(--e360-primary)]">{up.value_at_stake.range_label}</div>
              </div>
            )}
          </div>
          {(up.reasons || []).length > 0 && (
            <ul className="mt-2.5 space-y-1">
              {up.reasons.map((r, i) => (
                <li key={i} className="text-[11px] text-zinc-500 flex items-start gap-1.5"><ArrowUpRight size={12} className="text-[var(--e360-primary)] mt-0.5 shrink-0" />{r}</li>
              ))}
            </ul>
          )}
          <button data-testid="e360-promote-full" onClick={() => {
            window.dispatchEvent(new CustomEvent("e360-promote-full", { detail: {
              clientId, clientName: spine.client_name, streamIds: up.self_declared_stream_ids || [] } }));
            toast.success("Full assessment pre-filled — complete the few required foundational fields, then score the streams");
          }} className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-medium text-white bg-[var(--e360-primary)] px-3.5 py-2 rounded-lg hover:opacity-90">
            <Sparkles size={13} /> Start Full assessment (pre-filled)
          </button>
        </div>
      )}

      {/* Next-best assessment cross-sell */}
      {recs?.length > 0 && (
        <div className="mb-6" data-testid="e360-next-best">
          <div className="text-[11px] tracking-[0.16em] uppercase text-zinc-500 mb-2 flex items-center gap-1">
            <Sparkles size={12} className="text-[var(--e360-primary)]" /> Recommended next assessment
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {recs.map((r) => (
              <div key={`${r.kind}-${r.sub_brand}`} data-testid={`e360-rec-${r.sub_brand}`}
                className="border border-[var(--e360-primary)]/30 bg-[var(--e360-primary)]/5 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-[12px] font-semibold text-black">{r.action_label}</span>
                  <span className={`text-[9px] uppercase tracking-wider rounded-full px-2 py-0.5 ${r.kind === "deepen" ? "bg-amber-100 text-amber-700" : "bg-violet-100 text-[var(--e360-primary)]"}`}>
                    {r.kind === "deepen" ? "Deepen" : "White space"}
                  </span>
                </div>
                <p className="text-[12px] text-zinc-600 mt-1.5 leading-snug">{r.reason}</p>
                {r.indicative_label && (
                  <div className="mt-2 text-[12px] font-medium text-[var(--e360-primary)] flex items-center gap-1">
                    <ArrowUpRight size={13} /> ~{r.indicative_label} indicative opportunity
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-lens movement since the previous snapshot — proof of impact */}
      {trend?.lens_deltas?.length > 0 && (
        <div className="mb-6" data-testid="e360-lens-deltas">
          <div className="text-[11px] tracking-[0.16em] uppercase text-zinc-500 mb-2">Movement since last snapshot</div>
          <div className="flex flex-wrap gap-2">
            {trend.lens_deltas.map((d) => (
              <span key={d.sub_brand} data-testid={`e360-lens-delta-${d.sub_brand}`}
                className={`text-[11px] rounded-full px-2.5 py-1 inline-flex items-center gap-1 ${d.change > 0 ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-rose-50 text-rose-700 border border-rose-200"}`}>
                {d.change > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                {d.sub_brand} {d.change > 0 ? "+" : ""}{d.change}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Functional lenses */}
      <div className="text-[11px] tracking-[0.16em] uppercase text-zinc-500 mb-2 flex items-center gap-2">
        Functional lenses (12)
        <span className="text-[10px] tracking-normal normal-case text-zinc-400">· one 0–100 maturity index, four bands</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5 mb-6" data-testid="e360-spine-lenses">
        {lenses.map((l) => (
          <div key={l.sub_brand} data-testid={`e360-lens-${l.sub_brand}`} className="flex items-center gap-3">
            <div className="w-28 shrink-0">
              <div className="text-[12.5px] font-medium text-black truncate" title={l.area}>{l.sub_brand}</div>
              <div className="text-[10px] text-zinc-400 truncate">{l.area}</div>
            </div>
            {l.status === "assessed" ? (
              <>
                <div className="flex-1 h-2.5 bg-zinc-100 rounded-full overflow-hidden">
                  <div className={`h-2.5 rounded-full ${barColor(l.band)}`} style={{ width: `${l.normalised}%` }} />
                </div>
                <CheckCircle2 size={13} className="text-emerald-500 shrink-0" />
                <span className="text-[11px] text-zinc-500 w-10 text-right shrink-0" title={`${l.maturity}/5 native`}>{l.normalised}</span>
                <span className={`text-[10px] rounded-full px-2 py-0.5 shrink-0 ${bandColor(l.band)}`}>{l.band}</span>
                {l.vs_reinforced && <span title="Corroborated by a ValueStream360 self-assessment" data-testid={`e360-vs-reinforced-${l.sub_brand}`} className="text-[10px] text-[var(--e360-primary)] shrink-0">↔ VS</span>}
              </>
            ) : l.vs_indicative ? (
              <>
                <div className="flex-1 h-2.5 bg-zinc-100 rounded-full overflow-hidden">
                  <div className={`h-2.5 rounded-full opacity-50 ${barColor(l.vs_band)}`} style={{ width: `${l.vs_normalised}%` }} />
                </div>
                <CircleDashed size={13} className="text-[var(--e360-primary)] shrink-0" />
                <span className="text-[11px] text-[var(--e360-primary)] shrink-0" data-testid={`e360-vs-indicative-${l.sub_brand}`} title="Indicative read from a linked ValueStream360 self-assessment">VS ~{l.vs_normalised}</span>
              </>
            ) : (
              <>
                <div className="flex-1 h-2.5 bg-zinc-50 border border-dashed border-zinc-200 rounded-full" />
                <CircleDashed size={13} className="text-zinc-300 shrink-0" />
                <span className="text-[11px] text-zinc-400 shrink-0">{l.indicative_label ? `~${l.indicative_label}` : "white space"}</span>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Value-stream maturity (from the joined-up engine heatmap) */}
      {streams?.length > 0 && (
        <div data-testid="e360-spine-streams">
          <div className="text-[11px] tracking-[0.16em] uppercase text-zinc-500 mb-2">Value-stream maturity (engine)</div>
          <div className="flex flex-wrap gap-2">
            {streams.map((s) => (
              <span key={s.stream} className={`text-[11px] rounded-full px-3 py-1 ${bandColor(s.band)}`} title={s.note || ""}>
                {s.stream.replace(/_/g, " ")} · {s.band}{s.index != null ? ` ${s.index}` : ""}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
